import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { collection, getDocs, addDoc, deleteDoc, doc } from 'firebase/firestore';
import './DiscountConfig.css';

import Notification from './Notification';
import Spinner from './Spinner';

const DiscountConfig = () => {
  const [discounts, setDiscounts] = useState([]);
  const [discountData, setDiscountData] = useState({
    type: '',
    keyword: '',
    minQuantity: '',
    minAmount: '',
    percentage: '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [notification, setNotification] = useState({ message: '', show: false });

  useEffect(() => {
    const fetchDiscounts = async () => {
      setIsLoading(true);
      try {
        const querySnapshot = await getDocs(collection(db, 'discounts'));
        const fetchedDiscounts = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setDiscounts(fetchedDiscounts);
        setIsLoading(false);
      } catch (error) {
        setIsLoading(false);
        showNotification('Error al cargar los descuentos.');
        console.error("Error fetching discounts: ", error);
      }
    };

    fetchDiscounts();
  }, []);

  const showNotification = (message) => {
    setNotification({ message, show: true });
    setTimeout(() => setNotification({ message: '', show: false }), 3000);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setDiscountData(prevData => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleAddDiscount = async () => {
    const { type, keyword, minQuantity, minAmount, percentage } = discountData;

    if (
      percentage === '' || 
      (type === 'keyword' && keyword === '') || 
      (type === 'quantity' && minQuantity === '') || 
      (type === 'amount' && minAmount === '')
    ) {
      showNotification('Por favor complete todos los campos.');
      return;
    }

    setIsLoading(true);
    try {
      let newDiscount = { type, percentage: Number(percentage) };

      if (type === 'keyword') {
        newDiscount.keyword = keyword;
      } else if (type === 'quantity') {
        newDiscount.minQuantity = Number(minQuantity);
      } else if (type === 'amount') {
        newDiscount.minAmount = Number(minAmount);
      }

      await addDoc(collection(db, 'discounts'), newDiscount);
      setDiscounts([...discounts, newDiscount]);

      setDiscountData({
        type: '',
        keyword: '',
        minQuantity: '',
        minAmount: '',
        percentage: '',
      });

      setIsLoading(false);
      showNotification('Descuento agregado exitosamente.');
    } catch (error) {
      setIsLoading(false);
      showNotification('Error al agregar el descuento.');
      console.error("Error adding discount: ", error);
    }
  };

  const handleDeleteDiscount = async (id) => {
    setIsLoading(true);
    try {
      await deleteDoc(doc(db, 'discounts', id));
      setDiscounts(discounts.filter(discount => discount.id !== id));
      setIsLoading(false);
      showNotification('Descuento eliminado exitosamente.');
    } catch (error) {
      setIsLoading(false);
      showNotification('Error al eliminar el descuento.');
      console.error("Error deleting discount: ", error);
    }
  };

  return (
    <div className="discount-config">
      <Notification message={notification.message} show={notification.show} />
      {isLoading && <Spinner />}
      <h2 className="discount-config-title">Configuración de Descuentos</h2>
      
      <div className="discount-form">
        <div className="discount-form-row">
          <div className="discount-field">
            <label htmlFor="discount-type">Tipo de descuento</label>
            <select
              id="discount-type"
              name="type"
              value={discountData.type}
              onChange={handleInputChange}
              className="discount-input"
            >
              <option value="">Seleccionar tipo</option>
              <option value="keyword">Palabra clave</option>
              <option value="quantity">Cantidad mínima</option>
              <option value="amount">Monto mínimo</option>
            </select>
          </div>
          <div className="discount-field">
            <label htmlFor="discount-percentage">Porcentaje (%)</label>
            <input
              id="discount-percentage"
              type="number"
              name="percentage"
              min="1"
              max="100"
              placeholder="Ej: 15"
              value={discountData.percentage}
              onChange={handleInputChange}
              className="discount-input"
            />
          </div>
        </div>

        {discountData.type === 'keyword' && (
          <div className="discount-form-row">
            <div className="discount-field">
              <label htmlFor="discount-keyword">Palabra clave</label>
              <input
                id="discount-keyword"
                type="text"
                name="keyword"
                placeholder="Ej: PRIBALL"
                value={discountData.keyword}
                onChange={handleInputChange}
                className="discount-input"
              />
            </div>
          </div>
        )}

        {discountData.type === 'quantity' && (
          <div className="discount-form-row">
            <div className="discount-field">
              <label htmlFor="discount-quantity">Cantidad mínima</label>
              <input
                id="discount-quantity"
                type="number"
                name="minQuantity"
                min="1"
                placeholder="Ej: 5"
                value={discountData.minQuantity}
                onChange={handleInputChange}
                className="discount-input"
              />
            </div>
          </div>
        )}

        {discountData.type === 'amount' && (
          <div className="discount-form-row">
            <div className="discount-field">
              <label htmlFor="discount-amount">Monto mínimo</label>
              <input
                id="discount-amount"
                type="number"
                name="minAmount"
                min="0"
                placeholder="Ej: 1000"
                value={discountData.minAmount}
                onChange={handleInputChange}
                className="discount-input"
              />
            </div>
          </div>
        )}

        <div className="discount-form-actions">
          <button type="button" onClick={handleAddDiscount} className="discount-button">
            Agregar descuento
          </button>
        </div>
      </div>

      <div className="discount-list">
        <h3 className="discount-list-title">Descuentos actuales</h3>
        {discounts.length === 0 ? (
          <p className="discount-list-empty">No hay descuentos configurados. Agrega uno desde el formulario.</p>
        ) : (
          discounts.map(discount => (
            <div key={discount.id} className="discount-item">
              <span className="discount-item-badge">
                {discount.type === 'keyword' && 'Palabra clave'}
                {discount.type === 'quantity' && 'Cantidad mín.'}
                {discount.type === 'amount' && 'Monto mín.'}
              </span>
              <div className="discount-item-details">
                {discount.type === 'keyword' && (
                  <span className="discount-detail"><strong>{discount.keyword}</strong></span>
                )}
                {discount.type === 'quantity' && (
                  <span className="discount-detail"><strong>{discount.minQuantity}</strong> unidades</span>
                )}
                {discount.type === 'amount' && (
                  <span className="discount-detail"><strong>${discount.minAmount}</strong></span>
                )}
                <span className="discount-detail">{discount.percentage}% desc.</span>
              </div>
              <div className="discount-item-actions">
                <button type="button" onClick={() => handleDeleteDiscount(discount.id)} className="discount-delete-button" aria-label="Eliminar descuento">
                  Eliminar
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default DiscountConfig;